import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const experiences = [
  {
    company: "Oryo Limited",
    duration: "2016–2025",
    logo: "/experience/oryo.png",
    roles: [
      {
        title: "Snr Manager – Software & Hardware",
        period: "2022–2025",
        details: [
          "Architected a real-time fleet monitoring system for fuel theft detection and telemetry analytics.",
          "Built internal tools including Audit Platform, CBT Exam System, and Heartbeat Monitor for tech operations.",
          "Led reverse engineering of vehicle CAN data and diagnostics optimization.",
          "Managed Vultr and AWS infrastructure with Terraform, Ansible, ELK, Prometheus, and Grafana.",
          "Oversaw software lifecycle from user requirement gathering to deployment and scaling."
        ]
      },
      {
        title: "Lead Systems Engineer",
        period: "2018–2021",
        details: [
          "Deployed fleet monitoring systems for Dangote, Coca-Cola, Shell, and Mota-Engil.",
          "Established a 24/7 NOC to monitor 20,000+ devices with real-time alerts.",
          "Conducted GPS/telematics analysis to improve behavior insights and reduce operational inefficiencies.",
          "Worked cross-functionally with OEMs and clients to meet custom fleet requirements."
        ]
      },
      {
        title: "Technical Support Engineer",
        period: "2016–2017",
        details: [
          "Provided on-site support for GPS/IoT device installation, configuration, and maintenance.",
          "Developed troubleshooting documentation and supported nationwide field deployments."
        ]
      },
      {
        title: "Key Achievements",
        period: "",
        achievements: [
          "Pioneered Africa’s first dual-tank fuel monitoring solution (>$100M in revenue).",
          "Reduced fuel theft by 75% (₦10B savings/year).",
          "Improved fuel efficiency from 1.8 km/L to 2.2 km/L (₦50B+ in savings).",
          "Deployed 20,000+ GPS tracking devices, analyzing over 20M telematics data points.",
          "Created tech-support/NOC model for uptime monitoring and alerting."
        ]
      }
    ]
  },
  {
    company: "Roie Technologies Ltd",
    duration: "2018–Present",
    logo: "/experience/roietrackers.jpeg",
    roles: [
      {
        title: "Co-Founder",
        period: "2018–Present",
        details: [
          "Led entrepreneurial strategy for telematics/GPS business in Nigeria’s IoT landscape.",
          "Planned and executed go-to-market strategies, promotional campaigns, and client acquisition plans.",
          "Coordinated business operations, stakeholder relations, and cross-functional execution."
        ]
      },
      {
        title: "Solution Architect",
        period: "2018–Present",
        details: [
          "Designed full-stack infrastructure, database schema, and secure networking architecture.",
          "Streamlined internal workflows through automation and integration across CI/CD and monitoring tools.",
          "Defined data modeling and role-based access for client dashboards and control panels."
        ]
      },
      {
        title: "Python Developer",
        period: "2018–Present",
        details: [
          "Built backend services in Python for user filtering, device registration, and event processing.",
          "Created APIs for subscription tracking, asset linking, and compliance checks.",
          "Implemented serverless logic using AWS Lambda to manage geofencing and NOC alerts."
        ]
      },
      {
        title: "Projects & Achievements",
        period: "",
        achievements: [
          "Designed an ISP migration signup portal to manage regional deployment by field agents.",
          "Integrated fintech APIs to automate fleet subscription management and invoice generation.",
          "Built a route violation tracking system with real-time alerting and analytics dashboards.",
          "Engineered a modular NOC monitoring platform using FastAPI, PostgreSQL, and WebSocket streams.",
          "Developed multi-tenant admin/operator roles for enterprise-level fleet analytics."
        ]
      }
    ]
  },
  {
    title: "Volunteer Tech Tutor – PALS Edmonton",
    date: "2025–Present",
    logo: "/experience/pals.png",
    details: [
      "Teach digital literacy to seniors and new immigrants.",
      "Simplify technology use for everyday needs like banking and email."
    ]
  }
];

const Experience = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const toggleIndex = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section id="experience" className="my-20 max-w-4xl mx-auto px-4">
      <motion.h2
        className="text-3xl font-bold mb-6 text-softPurple"
        initial={{ opacity: 0, x: -50 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
      >
        Experience
      </motion.h2>
      <div className="space-y-10">
        {experiences.map((exp, i) => (
          <motion.div
            key={i}
            className="bg-gray-800 rounded-xl p-6 shadow-md hover:shadow-glow transition-shadow"
            whileHover={{ scale: 1.02 }}
          >
            <div className="flex items-center mb-2">
              {exp.logo && (
                <img
                  src={exp.logo}
                  alt="logo"
                  className="w-10 h-10 object-contain mr-3 rounded-full border border-gray-700"
                />
              )}
              <h3 className="text-xl font-semibold text-white">
                {exp.company || exp.title}
              </h3>
            </div>
            <p className="text-sm text-gray-400 mb-4">{exp.duration || exp.date}</p>
            {exp.roles ? (
              exp.roles.map((role, j) => (
                <div key={j} className="mb-4">
                  <button
                    onClick={() => toggleIndex(`${i}-${j}`)}
                    className="w-full text-left text-md font-semibold text-softPurple hover:underline focus:outline-none"
                  >
                    {role.title}
                  </button>
                  {role.period && (
                    <p className="text-sm text-gray-400">{role.period}</p>
                  )}
                  {(role.title === "Key Achievements" || role.title === "Projects & Achievements") && role.achievements && (
                    <div className="mt-4 flex flex-wrap gap-2">
                      {role.achievements.map((item, idx) => (
                        <span
                          key={idx}
                          className="bg-softPurple text-white text-xs px-3 py-1 rounded-full"
                        >
                          {item}
                        </span>
                      ))}
                    </div>
                  )}
                  <AnimatePresence>
                    {openIndex === `${i}-${j}` && role.details && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="overflow-hidden"
                      >
                        <ul className="list-disc list-inside text-gray-300 space-y-1 mt-1">
                          {role.details.map((item, idx) => (
                            <li key={idx}>{item}</li>
                          ))}
                        </ul>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))
            ) : (
              <ul className="list-disc list-inside text-gray-300 space-y-2">
                {exp.details.map((item, idx) => (
                  <li key={idx}>{item}</li>
                ))}
              </ul>
            )}
          </motion.div>
        ))}
      </div>
    </section>
  );
};

export default Experience;

